import PropTypes from 'prop-types';
import React from 'react';
import { useDropzone } from 'react-dropzone';
import { nanoid } from 'nanoid';
import {
	IconButton, Tooltip, Typography, styled
} from '@mui/material';
import { Box } from '@mui/system';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { toBase64 } from '../../utils/base64with-promise.js';
import { DropzoneContainer } from '../../Styles/styles.js';
import customToast from './custom-toast.js';

const infoText = 'Maximum of 5 files allowed.';

const PlusSign = styled(Typography)(({ disabled }) => ({
	fontSize: '20px',
	backgroundColor: disabled ? '#D3D3D3' : '#B7D423',
	borderRadius: '50%',
	height: '30px',
	width: '30px',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	color: '#000',
	pointerEvents: disabled ? 'none' : 'auto',
	'&:hover': {
		backgroundColor: disabled ? '#D3D3D3' : '#45A049'
	}
}));

function CustomUpload({
	isClicked,
	dropzoneHeight,
	attachedfiles,
	acceptedFileTypes,
	maxFileSize,
	handleAttachmentSave,
	dropzoneEnabled,
	setDropzoneEnabled,
	errorMessage,
	setErrorMessage
}) {
	const maxTotalFiles = 5;

	const validateFile = (file) => {
		const errors = [];
		if (!acceptedFileTypes.includes(file.type)) {
			errors.push(`File type '${file.type}' is not allowed.`);
		}

		if (file.size > maxFileSize) {
			errors.push('File size exceeds the maximum limit (5MB).');
		}

		return errors;
	};

	const filterDuplicateFiles = (files) =>
		files.filter(
			(file) => !attachedfiles?.some((existing) => existing.name === file.name)
		);

	const generateFilePayload = async (file) => {
		const id = nanoid(5);
		const filebase64 = await toBase64(file);
		return {
			id,
			name: file.name,
			content: file,
			dataUrl: filebase64,
			type: file.type,
			enableDelete: true
		};
	};

	const { showToast } = customToast();

	const onDrop = async (acceptedFiles) => {
		const existingFileCount = attachedfiles?.length || 0;
		const totalFilesAfterUpload = existingFileCount + acceptedFiles.length;

		if (totalFilesAfterUpload > maxTotalFiles) {
			showToast('error', 'You can attach a maximum of 5 files.');
			return;
		}

		try {
			const remainingSlots = maxTotalFiles - existingFileCount;
			const uniqueAcceptedFiles = filterDuplicateFiles(acceptedFiles);
			const duplicateFiles = acceptedFiles.filter((file) =>
				attachedfiles?.some((existing) => existing.name === file.name)
			);

			if (duplicateFiles.length > 0) {
				const duplicateFileNames = duplicateFiles.map((file) => file.name);
				if (duplicateFiles.length === 1) {
					showToast('error', `This file is already uploaded: ${duplicateFileNames[0]}`);
				} else {
					showToast('error', `These files are already uploaded: ${duplicateFileNames.join(', ')}`);
				}
			}

			const accumulatedErrors
				= duplicateFiles.length > 0
					? [
						`Duplicate files found: ${duplicateFiles.map((file) => file.name).join(', ')}`
					]
					: [];

			const newPayload = [];
			let filesToAdd = Math.min(remainingSlots, uniqueAcceptedFiles.length);

			for (const file of uniqueAcceptedFiles.slice(0, filesToAdd)) {
				const validationErrors = validateFile(file);
				if (validationErrors.length > 0) {
					accumulatedErrors.push(...validationErrors);
					filesToAdd--;
					continue;
				}

				const payload = await generateFilePayload(file);
				newPayload.push(payload);
			}

			handleAttachmentSave(newPayload);

			setErrorMessage(
				accumulatedErrors.length > 0 ? accumulatedErrors.join(' ') : ''
			);

			if (remainingSlots <= filesToAdd) {
				setDropzoneEnabled(false);
				setErrorMessage('Max files attached');
			}
		} catch (error) {
			console.error('Error uploading file:', error);
		}
	};

	const { getRootProps, getInputProps } = useDropzone({
		onDrop(acceptedFiles) {
			(async () => {
				await onDrop(acceptedFiles);
			})();
		},
		disabled: !dropzoneEnabled,
		accept: {
			'application/pdf': ['.pdf'],
			'image/png': ['.png'],
			'image/jpeg': ['.jpg', '.jpeg']
		}
	});

	return (
		<div
			{...getRootProps()}
			style={{
				opacity: dropzoneEnabled ? '1' : '0.5',
				height: '100%',
				cursor: dropzoneEnabled ? 'pointer' : 'not-allowed'
			}}
		>
			<input {...getInputProps()} />
			<DropzoneContainer
				className={isClicked ? 'blinking' : ''}
				style={{
					height: dropzoneHeight,
					cursor: dropzoneEnabled ? 'pointer' : 'not-allowed'
				}}
			>
				<PlusSign disabled={!dropzoneEnabled}> + </PlusSign>
				{errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
				<div
					className='medium6'
					style={{ marginTop: '10px', marginBottom: '10px' }}
				>
					Drag and drop file here <br />
					to start workflow
				</div>
				<div className='medium5' style={{ color: '#E96666' }}>
					Formats allowed: PDF, .jpeg, .png, .jpg
				</div>
				<Box display='flex' alignItems='center' mt={0.5}>
					<Typography className='medium5' sx={{ color: '#E96666' }}>
    Maximum File Size: 5MB
					</Typography>
					<Tooltip title={dropzoneEnabled ? infoText : ''} placement='right'>
						<IconButton size='small' sx={{ ml: 0.5, p: 0, cursor: dropzoneEnabled ? 'pointer' : 'not-allowed' }}>
							<InfoOutlinedIcon fontSize='small' />
						</IconButton>
					</Tooltip>
				</Box>
			</DropzoneContainer>
		</div>
	);
}

export default CustomUpload;

CustomUpload.propTypes = {
	isClicked: PropTypes.any,
	dropzoneHeight: PropTypes.any,
	attachedfiles: PropTypes.any,
	acceptedFileTypes: PropTypes.any,
	maxFileSize: PropTypes.any,
	handleAttachmentSave: PropTypes.any,
	dropzoneEnabled: PropTypes.any,
	setDropzoneEnabled: PropTypes.any,
	errorMessage: PropTypes.any,
	setErrorMessage: PropTypes.any
};
